import { text, toArray } from './feed'

/**
 * Cricket is the one GoalServe team sport the generic {@link collectMatches}
 * walk cannot render: a match has no single score but one to four innings per
 * side ("245/6 (48.2)", Tests "312 & 145/3"), its status is a sentence
 * ("Australia won by 5 wickets", "Khan RL lead by 405 runs.") and the format
 * (T20 / ODI / Test) decides how a row should read. This module knows the
 * `cricket/livescore` node (also stored verbatim in `cricket-matches.raw` and
 * replayed by `/v1/cricket/livescores?day=`) and the lighter `cricket/schedule`
 * fixture node, and turns both into one shape the cricket centre renders.
 *
 * Verified against the live feed (2026-09): a match node carries `type`
 * (TEST/ODI/T20), `status` ("Not Started" | "Not covered Live" | "Finished" …),
 * `date` + `date_range` (Tests span days), `venue`, `localteam`/`visitorteam`
 * `{id,name,totalscore,stat,winner}` — `totalscore` is the first innings
 * ("547/9d", the `d` = declared), `stat` the second — `inning[]` with
 * `{inningnum, team: localteam|visitorteam, total:{tot:"547 ( 122.4 )", wickets}}`,
 * `comment.post` (the result sentence once finished, a lead/"chose to bat" note
 * otherwise, sometimes an unrendered "{{MATCH_START_HOURS}}" template) and
 * `matchinfo.info[]` name/value pairs (Toss, Venue, Man Of Match).
 */

export type CricketFormat = 'T20' | 'ODI' | 'TEST' | ''
export type CricketState = 'scheduled' | 'live' | 'finished'

export interface CricketInnings {
  runs: string
  /** '' when the feed has no wicket count; '10' = all out. */
  wickets: string
  /** Overs bowled, "48.2"; '' when unknown (schedule / store-only rows). */
  overs: string
  declared: boolean
}

export interface CricketMatch {
  id: string
  /** Series name with the " - T20/ODI/Test" suffix stripped. */
  series: string
  seriesId: string
  format: CricketFormat
  home: string
  away: string
  homeId?: string
  awayId?: string
  /** dd.MM.yyyy — the start day for multi-day matches. */
  date: string
  /** dd.MM.yyyy — the last scheduled day of a Test, when the feed says. */
  dateEnd?: string
  /** HH:mm (UTC) */
  time: string
  /** The feed's own status string, verbatim. */
  status: string
  state: CricketState
  homeInnings: CricketInnings[]
  awayInnings: CricketInnings[]
  homeWinner: boolean
  awayWinner: boolean
  /** "Australia won by 5 wickets (with 19 balls remaining)" once finished. */
  result: string
  /** In-play note: "Khan RL lead by 405 runs." — '' when the feed has none. */
  note: string
  venue: string
  /** "Australia, elected to bat first" — '' before the toss. */
  toss: string
  /** "3rd Youth ODI", "1st T20I" — schedule feed only. */
  matchNum: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const FORMAT_SUFFIX = /\s*[-–]\s*(test|odi|t20i?|t10)\s*$/i

/** "County Championship Division One - Test" → "County Championship Division One". */
export function stripFormatSuffix(name: string): string {
  return name.replace(FORMAT_SUFFIX, '').trim()
}

/** Feed `type` ("TEST", "ODI", "T20") or the series-name suffix → format. */
export function cricketFormat(type: string, seriesName: string): CricketFormat {
  const probe = `${type} ${seriesName.match(FORMAT_SUFFIX)?.[1] ?? ''}`.toUpperCase()
  if (/T20|T10|TWENTY/.test(probe)) return 'T20'
  if (/ODI|ONE.?DAY|LIST A/.test(probe)) return 'ODI'
  if (/TEST|FIRST.?CLASS|\b[34].?DAY/.test(probe)) return 'TEST'
  return ''
}

const SCHEDULED = /not started|^ns$|scheduled|^\d{1,2}:\d{2}$/i
const FINISHED = /finish|result|\bwon\b|abandon|cancel|postpon|\bdraw|\btie/i

export function cricketState(status: string): CricketState {
  const s = status.trim()
  if (!s || SCHEDULED.test(s)) return 'scheduled'
  if (FINISHED.test(s)) return 'finished'
  return 'live'
}

/** "547 ( 122.4 )" → runs + overs; "302/6" / "547/9d" → runs + wickets (+ declared). */
function parseTotal(tot: string): { runs: string; overs: string } {
  const m = tot.match(/^\s*(\d+)\s*(?:\(\s*([\d.]+)\s*\))?/)
  return { runs: m?.[1] ?? '', overs: m?.[2] ?? '' }
}
function parseScoreString(score: string): CricketInnings | null {
  const m = score.match(/^\s*(\d+)(?:\s*\/\s*(\d+))?\s*(d)?\s*$/i)
  if (!m) return null
  return { runs: m[1], wickets: m[2] ?? '', overs: '', declared: Boolean(m[3]) }
}

/** The side's innings, in order, from `inning[]`; falls back to the side's
 *  `totalscore` / `stat` strings when the node carries no innings detail. */
function inningsFor(node: Record<string, unknown>, sideKey: 'localteam' | 'visitorteam'): CricketInnings[] {
  const side = node[sideKey]
  const sideScores = [text(side, 'totalscore'), text(side, 'stat')].filter(Boolean)
  const entries = toArray(node.inning)
    .filter((i) => text(i, 'team') === sideKey)
    .sort((a, b) => Number(text(a, 'inningnum')) - Number(text(b, 'inningnum')))
  if (entries.length === 0) {
    return sideScores.map(parseScoreString).filter((i): i is CricketInnings => i !== null)
  }
  return entries.map((entry, index) => {
    const total = isRecord(entry) ? entry.total : undefined
    const { runs, overs } = parseTotal(text(total, 'tot'))
    return {
      runs,
      wickets: text(total, 'wickets'),
      overs,
      declared: /d\s*$/i.test(sideScores[index] ?? ''),
    }
  })
}

function infoValue(node: Record<string, unknown>, name: string): string {
  const info = isRecord(node.matchinfo) ? node.matchinfo.info : undefined
  const hit = toArray(info).find((i) => text(i, 'name').toLowerCase() === name.toLowerCase())
  return hit ? text(hit, 'value') : ''
}

/** GoalServe leaves unrendered "{{MATCH_START_HOURS}}" templates in `comment.post`. */
function cleanComment(post: string): string {
  return post.includes('{{') ? '' : post.trim()
}

export function parseCricketMatch(node: unknown, seriesName = '', seriesId = ''): CricketMatch | null {
  if (!isRecord(node)) return null
  const home = node.localteam
  const away = node.visitorteam
  if (!isRecord(home) || !isRecord(away)) return null
  const id = text(node, 'id')
  if (!id) return null
  const status = text(node, 'status')
  const state = cricketState(status)
  const post = cleanComment(text(node.comment, 'post'))
  const [dateStart, dateEnd] = text(node, 'date_range').split('-').map((s) => s.trim())
  return {
    id,
    series: stripFormatSuffix(seriesName),
    seriesId,
    format: cricketFormat(text(node, 'type'), seriesName),
    home: text(home, 'name'),
    away: text(away, 'name'),
    homeId: text(home, 'id') || undefined,
    awayId: text(away, 'id') || undefined,
    date: text(node, 'date') || dateStart || '',
    dateEnd: dateEnd || undefined,
    time: text(node, 'time'),
    status,
    state,
    homeInnings: inningsFor(node, 'localteam'),
    awayInnings: inningsFor(node, 'visitorteam'),
    homeWinner: text(home, 'winner') === 'True',
    awayWinner: text(away, 'winner') === 'True',
    result: state === 'finished' ? post : '',
    note: state === 'live' ? post : '',
    venue: text(node, 'venue') || infoValue(node, 'Venue'),
    toss: infoValue(node, 'Toss'),
    matchNum: text(node, 'match_num'),
  }
}

/**
 * Every match in a cricket feed: `scores.category[].match` (livescore, and the
 * store-rebuilt day feeds) or `fixtures.category[].match` (schedule). A
 * category is one series and names it; GoalServe collapses single matches to a
 * bare object, hence toArray at both levels.
 */
export function collectCricketMatches(payload: unknown): CricketMatch[] {
  if (!isRecord(payload)) return []
  const root = payload.scores ?? payload.fixtures
  if (!isRecord(root)) return []
  const out: CricketMatch[] = []
  for (const category of toArray(root.category)) {
    if (!isRecord(category)) continue
    const name = text(category, 'name')
    const id = text(category, 'id')
    for (const node of toArray(category.match)) {
      const match = parseCricketMatch(node, name, id)
      if (match) out.push(match)
    }
  }
  return out
}

/** "245/6" · "306" (all out) · "547/9d" (declared). */
export function formatInnings(innings: CricketInnings): string {
  const allOut = innings.wickets === '10'
  const wk = innings.wickets && !allOut ? `/${innings.wickets}` : ''
  return `${innings.runs}${wk}${innings.declared ? 'd' : ''}`
}

/**
 * A side's scoreline: innings joined with " & " (Tests), the overs of the most
 * recent innings in parentheses when known — "547/9d & 164/1 (24)". '' when
 * the side has not batted.
 */
export function formatScoreline(innings: CricketInnings[]): string {
  if (innings.length === 0) return ''
  const parts = innings.map(formatInnings)
  const last = innings[innings.length - 1]
  const overs = last.overs ? ` (${last.overs})` : ''
  return `${parts.join(' & ')}${overs}`
}
